import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Facebook, ExternalLink, ThumbsUp, MessageCircle, Share2, Calendar, MapPin, Play, X } from 'lucide-react';
import { apiFetch } from '../lib/api';

const FACEBOOK_PAGE_URL = "https://www.facebook.com/people/Heartland-Plains-Chasing/61589810947137/";
const PROFILE_IMAGE = "/favicon.png";
const TEAM_NAME = "Heartland Plains Chasing";
const POST_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'America/Chicago',
  timeZoneName: 'short',
});

const formatPostDate = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }

  const rawValue = value.trim();
  const normalizedValue = rawValue.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) {
    return rawValue;
  }

  return POST_DATE_FORMATTER.format(date);
};

const FacebookFeed = () => {
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadPosts = async () => {
      try {
        const response = await apiFetch('/api/facebook/posts?limit=3&auto_import=true');
        if (!response.ok) {
          if (isMounted) {
            setFeaturedPosts([]);
            setLoadError(true);
          }
          return;
        }

        const posts = await response.json();
        if (isMounted) {
          setFeaturedPosts(Array.isArray(posts) ? posts : []);
          setLoadError(false);
        }
      } catch {
        if (isMounted) {
          setFeaturedPosts([]);
          setLoadError(true);
        }
      }
    };

    loadPosts();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!activeVideo) {
      return undefined;
    }

    const scrollY = window.scrollY;
    const previousPosition = document.body.style.position;
    const previousTop = document.body.style.top;
    const previousWidth = document.body.style.width;
    const previousOverflow = document.body.style.overflow;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveVideo(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.position = previousPosition;
      document.body.style.top = previousTop;
      document.body.style.width = previousWidth;
      document.body.style.overflow = previousOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [activeVideo]);

  return (
    <>
    <section id="connect" className="py-24 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-red-500/10 rounded-full filter blur-[150px]" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-red-500/5 rounded-full filter blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ amount: 0.2 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-red-500/20 text-red-400 rounded-full text-sm font-medium mb-4">
            Stay Connected
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Latest Updates
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            Follow us on Facebook for real-time storm chase updates and behind-the-scenes content
          </p>

          <a
            href={FACEBOOK_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 hover:bg-red-500 text-white font-semibold rounded-full transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-red-500/25"
          >
            <Facebook size={22} />
            Follow Us on Facebook
            <ExternalLink size={18} />
          </a>
        </motion.div>

        {/* Posts Feed */}
        {loadError ? (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ amount: 0.2 }}
            className="text-center p-10 rounded-2xl bg-gray-900/50 border border-gray-800"
          >
            <Facebook size={40} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">
              Unable to retrieve Facebook posts, please try again later.
            </p>
          </motion.div>
        ) : featuredPosts.length > 0 ? (
          <div className="space-y-6">
            {featuredPosts.map((post, index) => {
              const imageList = Array.isArray(post.images) && post.images.length > 0
                ? post.images
                : (post.image ? [post.image] : []);
              const videoList = Array.isArray(post.videos) && post.videos.length > 0
                ? post.videos
                : (post.video ? [post.video] : []);
              const videoEmbedUrl = typeof post.videoEmbedUrl === 'string' && post.videoEmbedUrl
                ? post.videoEmbedUrl
                : null;
              const videoThumbnailUrl = typeof post.videoThumbnailUrl === 'string' && post.videoThumbnailUrl
                ? post.videoThumbnailUrl
                : null;
              const engagementUrl = post.sourceUrl || post.permalink_url || FACEBOOK_PAGE_URL;
              const hasEngagementCounts = [post.likes, post.comments, post.shares]
                .every((value) => typeof value === 'number' && Number.isFinite(value));
              const displayDate = formatPostDate(post.date);

              return (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ amount: 0.2 }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <div className="relative rounded-2xl overflow-hidden">
                    {/* Gradient border */}
                    <div className="absolute inset-0 bg-gradient-to-br from-red-500/30 via-gray-800 to-gray-800 rounded-2xl" />

                    {/* Card content */}
                    <div className="relative m-[1px] bg-gray-900/95 rounded-2xl overflow-hidden">
                      {/* Post header */}
                      <div className="flex items-center gap-4 p-5 border-b border-gray-800">
                        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 shadow-lg shadow-red-500/20">
                          <img
                            src={PROFILE_IMAGE}
                            alt={TEAM_NAME}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-bold">{TEAM_NAME}</h3>
                          <div className="flex items-center gap-3 text-gray-500 text-sm">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {displayDate}
                            </span>
                            {post.location && (
                              <span className="flex items-center gap-1">
                                <MapPin size={14} />
                                {post.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Post content */}
                      <div className="p-5">
                        {post.content && (
                          <p className="text-gray-200 text-lg leading-relaxed mb-4 whitespace-pre-line">
                            {post.content}
                          </p>
                        )}

                        {imageList.map((imageSrc, imageIndex) => (
                          <div key={`img-${post.id}-${imageIndex}`} className="rounded-xl overflow-hidden mb-4">
                            <img
                              src={imageSrc}
                              alt="Post"
                              className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                        ))}

                        {videoEmbedUrl ? (
                          <button
                            type="button"
                            onClick={() => setActiveVideo({ id: post.id, url: videoEmbedUrl })}
                            className="relative block w-full rounded-xl overflow-hidden mb-4 bg-black aspect-video text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                          >
                            {videoThumbnailUrl ? (
                              <img
                                src={videoThumbnailUrl}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover opacity-80"
                              />
                            ) : (
                              <iframe
                                title={`Facebook video preview ${post.id}`}
                                src={videoEmbedUrl}
                                scrolling="no"
                                frameBorder="0"
                                tabIndex="-1"
                                aria-hidden="true"
                                className="absolute inset-0 h-full w-full pointer-events-none opacity-80"
                              />
                            )}
                            <span className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/35 text-white">
                              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 shadow-lg shadow-black/40">
                                <Play size={30} fill="currentColor" className="ml-1" />
                              </span>
                              <span className="text-sm font-semibold uppercase tracking-wide">
                                Play video
                              </span>
                            </span>
                          </button>
                        ) : (
                          videoList.map((videoSrc, videoIndex) => (
                            <div key={`vid-${post.id}-${videoIndex}`} className="rounded-xl overflow-hidden mb-4 bg-black">
                              <video
                                src={videoSrc}
                                controls
                                controlsList="nodownload"
                                disablePictureInPicture
                                playsInline
                                preload="metadata"
                                className="w-full h-auto"
                              />
                            </div>
                          ))
                        )}

                        {/* Engagement stats */}
                        <div className="flex items-center gap-6 pt-4 border-t border-gray-800">
                          {hasEngagementCounts ? (
                            <>
                              <div className="flex items-center gap-2 text-gray-400">
                                <ThumbsUp size={18} className="text-red-400" />
                                <span className="text-sm font-medium">{post.likes}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-400">
                                <MessageCircle size={18} />
                                <span className="text-sm font-medium">{post.comments}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-400">
                                <Share2 size={18} />
                                <span className="text-sm font-medium">{post.shares}</span>
                              </div>
                            </>
                          ) : (
                            <a
                              href={engagementUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-sm font-medium text-red-300 hover:text-red-200 transition-colors"
                            >
                              View engagement on Facebook
                              <ExternalLink size={15} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ amount: 0.2 }}
            className="text-center p-10 rounded-2xl bg-gray-900/50 border border-gray-800"
          >
            <Facebook size={40} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">
              No Facebook posts are currently listed.
            </p>
          </motion.div>
        )}

        {/* View All CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ amount: 0.2 }}
          className="text-center mt-10"
        >
          <a
            href={FACEBOOK_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-full transition-all duration-200 border border-gray-700 hover:border-gray-600"
          >
            View All Posts on Facebook
            <ExternalLink size={16} />
          </a>
          <p className="text-gray-500 text-sm mt-4">
            See our full timeline and latest storm chasing adventures
          </p>
        </motion.div>
      </div>
    </section>
    {activeVideo && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 px-4 py-8"
        role="dialog"
        aria-modal="true"
        aria-label="Facebook video player"
        onClick={() => setActiveVideo(null)}
      >
        <div
          className="relative w-full max-w-5xl overflow-hidden rounded-xl bg-black shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setActiveVideo(null)}
            className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/75 text-white transition-colors hover:bg-black"
            aria-label="Close video"
          >
            <X size={22} />
          </button>
          <iframe
            title={`Facebook video ${activeVideo.id}`}
            src={activeVideo.url}
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            allowFullScreen
            scrolling="no"
            frameBorder="0"
            className="block w-full aspect-video"
          />
        </div>
      </div>
    )}
    </>
  );
};

export default FacebookFeed;
